import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getActiveRole } from '../utils/getActiveRole';
import './ConnectedServicesPage.css';

const ConnectedServicesPage = () => {
  const navigate = useNavigate();

  // Both cards are visible to the same roles — Agent, Broker, and TC (TC has e-signature-
  // provider parity with Agent) — but which ONE shows is decided by the agent's licensed
  // state: Colorado stays on DocuSign (the working, tested path); California and every
  // other state get the Lone Wolf connection option instead. Unknown/unset state defaults
  // to DocuSign rather than routing an unclassified agent to the newer integration.
  const eligibleForEsignatureProvider = (() => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return false;
      const parsed = JSON.parse(stored);
      const role = getActiveRole(parsed)?.toUpperCase();
      return role === 'AGENT' || role === 'BROKER' || role === 'TRANSACTION_COORDINATOR';
    } catch {
      return false;
    }
  })();

  const licenseState = (() => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return '';
      const parsed = JSON.parse(stored);
      return (parsed.licenseState || parsed.stateCode || parsed.state || '').toUpperCase();
    } catch {
      return '';
    }
  })();
  const isColorado = licenseState === 'CO';

  const canSeeDocuSign = eligibleForEsignatureProvider && (isColorado || licenseState === '');
  const canSeeLoneWolf = eligibleForEsignatureProvider && !isColorado && licenseState !== '';
  const currentUserEmail = (() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored).email : null;
    } catch {
      return null;
    }
  })();

  const [loneWolfLoading, setLoneWolfLoading] = useState(true);
  const [loneWolfStatus, setLoneWolfStatus] = useState({ exists: false, active: false });
  const [loneWolfEmailInput, setLoneWolfEmailInput] = useState('');
  const [loneWolfSavedEmail, setLoneWolfSavedEmail] = useState(null); // null = using login email, no override saved
  // True once the user has explicitly clicked Disconnect — suppresses the default
  // fall-back-to-login-email auto-check on future loads until they explicitly reconnect.
  const [loneWolfDisconnected, setLoneWolfDisconnected] = useState(false);
  const [savingLoneWolfEmail, setSavingLoneWolfEmail] = useState(false);
  const [loneWolfEmailError, setLoneWolfEmailError] = useState('');
  // Mirrors DocuSign's Change-Account flow: clicking it drops back into edit mode instead of
  // an OAuth redirect (there's nothing to redirect to — see canSeeLoneWolf comment above).
  const [isEditingLoneWolfEmail, setIsEditingLoneWolfEmail] = useState(false);

  // Form provider status — loaded once the connected account is confirmed Active (mirrors
  // DocuSign's Step 3 form-libraries check, but Lone Wolf's version is per-state, not a list:
  // GET /api/v1/lonewolf/form-provider-status resolves the caller's licensed state server-side
  // and checks it against their own accessible Lone Wolf libraries.
  const [loneWolfFormProviderStatus, setLoneWolfFormProviderStatus] = useState(null); // null = not yet loaded
  const [loneWolfFormProviderLoading, setLoneWolfFormProviderLoading] = useState(false);

  const [connectionLoading, setConnectionLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, authUrl: null, email: null, accountName: null });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null); // { status, userId }
  const [disconnecting, setDisconnecting] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteBlockedUntil, setInviteBlockedUntil] = useState(null); // rate-limit cooldown (mirrors mobile)
  const [setupUrlLoading, setSetupUrlLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // Form libraries — loaded automatically when invite is active (Step 3, mirrors mobile)
  const [formLibraries, setFormLibraries] = useState(null); // null = not yet loaded
  const [formLibrariesLoading, setFormLibrariesLoading] = useState(false);

  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      const timeoutId = setTimeout(() => { unsubscribe(); reject(new Error('Auth timeout')); }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
        } else {
          reject(new Error('Not authenticated'));
        }
      });
    });
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Step 3: fetch form libraries using user's licenseState → state → 'CA' (mirrors mobile)
  const loadFormLibraries = useCallback(async (firebaseToken) => {
    setFormLibrariesLoading(true);
    try {
      let userState = 'CA';
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          const profile = JSON.parse(stored);
          userState = profile.licenseState || profile.stateCode || profile.state || 'CA';
        }
      } catch (_) {}

      const response = await apiClient.get(API_ENDPOINTS.docusignFormLibraries, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
        params: userState ? { state: userState } : {},
      });
      const data = response?.data || response || [];
      const libraries = Array.isArray(data)
        ? data
        : (data.formsLibrarySummaries || data.formsLibraries || []);
      setFormLibraries(libraries);
    } catch (err) {
      console.error('Failed to fetch form libraries:', err);
      setFormLibraries([]); // show empty state on error
    } finally {
      setFormLibrariesLoading(false);
    }
  }, []);

  // Step 2: check invite status; if active → load form libraries (Step 3)
  const loadInviteStatus = useCallback(async (userEmail, firebaseToken) => {
    if (!userEmail) return;
    setInviteLoading(true);
    setFormLibraries(null);
    try {
      const response = await apiClient.get(API_ENDPOINTS.docusignRoomsUsers, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const summaries = response?.userSummaries || response?.data?.userSummaries || [];
      const match = summaries.find(u => u.email?.toLowerCase() === userEmail.toLowerCase());
      if (match) {
        setInviteInfo({ status: match.status, userId: match.userId });
        // Step 3: if invite is active, load form libraries
        if (match.status?.toLowerCase() === 'active') {
          loadFormLibraries(firebaseToken);
        }
      } else {
        setInviteInfo(null);
      }
    } catch (err) {
      console.error('Failed to load invite status:', err);
      setInviteInfo(null);
    } finally {
      setInviteLoading(false);
    }
  }, [loadFormLibraries]);

  // Step 1: check DocuSign connection; if connected → load invite status (Step 2)
  const loadConnectionStatus = useCallback(async () => {
    setConnectionLoading(true);
    setInviteInfo(null);
    setFormLibraries(null);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.docusignConnect, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const data = response?.data || response || {};
      setConnectionStatus({
        connected: data.connected === true,
        authUrl: data.authUrl || null,
        email: data.email || null,
        accountName: data.accountName || null,
      });
      if (data.connected && data.email) {
        loadInviteStatus(data.email, firebaseToken);
      }
    } catch (err) {
      console.error('Failed to load DocuSign connection status:', err);
      setConnectionStatus({ connected: false, authUrl: null, email: null, accountName: null });
    } finally {
      setConnectionLoading(false);
    }
  }, [getFirebaseToken, loadInviteStatus]);

  // Once Active, check whether a Lone Wolf form library is configured for the caller's own
  // licensed state — server resolves the state (agent profile, same source used elsewhere)
  // and checks it against the caller's own accessible Lone Wolf libraries.
  const loadLoneWolfFormProviderStatus = useCallback(async (firebaseToken) => {
    setLoneWolfFormProviderLoading(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.lonewolfFormProviderStatus, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const data = response?.data || response || {};
      setLoneWolfFormProviderStatus(data);
    } catch (err) {
      console.error('Failed to load Lone Wolf form provider status:', err);
      setLoneWolfFormProviderStatus({ configured: false, reason: 'Failed to check form provider status' });
    } finally {
      setLoneWolfFormProviderLoading(false);
    }
  }, []);

  // Lone Wolf status — two steps: (1) fetch the user's own saved loneWolfEmail override (if
  // any) from their profile, (2) check status using that override, falling back to their
  // Rialty login email when no override is saved. No multi-step connect flow like DocuSign
  // since there's nothing to OAuth-connect here (see canSeeLoneWolf comment above) — the user
  // just tells us which Lone Wolf account is theirs. When active, also loads form-provider
  // status (Step 3, mirrors DocuSign's form-libraries check).
  const loadLoneWolfStatus = useCallback(async () => {
    setLoneWolfLoading(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const meResponse = await apiClient.get(API_ENDPOINTS.userMe, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const meData = meResponse?.data || meResponse || {};
      const savedOverride = meData.loneWolfEmail || null;
      const disconnected = meData.loneWolfDisconnected === true;
      setLoneWolfSavedEmail(savedOverride);
      setLoneWolfDisconnected(disconnected);
      setLoneWolfEmailInput(savedOverride || currentUserEmail || '');

      // Explicitly disconnected with no override saved: do NOT auto-fall-back to checking the
      // login email — that would silently "reconnect" the user just from revisiting this page.
      // Show the plain not-connected state and wait for an explicit reconnect action instead.
      if (disconnected && !savedOverride) {
        setLoneWolfStatus({ exists: false, active: false });
        setLoneWolfFormProviderStatus(null);
        setLoneWolfLoading(false);
        return;
      }

      const emailToCheck = savedOverride || currentUserEmail;
      if (!emailToCheck) {
        setLoneWolfLoading(false);
        return;
      }
      const response = await apiClient.get(API_ENDPOINTS.lonewolfUserStatus(emailToCheck), {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const data = response?.data || response || {};
      const active = data.active === true;
      const exists = data.exists === true;
      setLoneWolfStatus({ exists, active });
      // Any non-Active landing state (Invited, or Not Invited/no match at all): default the
      // email TEXT FIELD to the user's own Rialty login email rather than re-showing the
      // already-saved override that just failed to resolve to Active — the saved override
      // stays visible in the message text above (loneWolfSavedEmail) for reference, but the
      // field itself gives them a fresh choice instead of just re-submitting the same value.
      // Does not touch what's actually persisted — that only changes when Save is clicked.
      if (!active) {
        setLoneWolfEmailInput(currentUserEmail || '');
      }
      if (active) {
        loadLoneWolfFormProviderStatus(firebaseToken);
      } else {
        setLoneWolfFormProviderStatus(null);
      }
    } catch (err) {
      console.error('Failed to load Lone Wolf status:', err);
      setLoneWolfStatus({ exists: false, active: false });
      setLoneWolfFormProviderStatus(null);
    } finally {
      setLoneWolfLoading(false);
    }
  }, [currentUserEmail, getFirebaseToken, loadLoneWolfFormProviderStatus]);

  const handleSaveLoneWolfEmail = async () => {
    const trimmed = loneWolfEmailInput.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setLoneWolfEmailError('Enter a valid email address.');
      return;
    }
    setLoneWolfEmailError('');
    setSavingLoneWolfEmail(true);
    // Snapshot what was connected before this attempt, so a non-Active result can be rolled
    // back to it rather than leaving a broken (Invited/no-match) connection persisted.
    const previousSaved = loneWolfSavedEmail;
    const previousDisconnected = loneWolfDisconnected;
    try {
      const firebaseToken = await getFirebaseToken();
      // Saving the same value as the login email is equivalent to no override — clear it
      // instead, so the "same as login" default stays intact if the login email ever changes.
      const toSave = trimmed && trimmed.toLowerCase() !== (currentUserEmail || '').toLowerCase() ? trimmed : '';
      // Must save BEFORE checking status: the status self-check only permits looking at your
      // own login email or your own already-saved override, so this candidate has to become
      // the saved override first for the check below to be allowed to look at it at all.
      // disconnected:false — any explicit (re)connect attempt clears the disconnected flag,
      // whether or not it ends up succeeding below.
      await apiClient.patch(
        API_ENDPOINTS.updateLoneWolfEmail,
        { loneWolfEmail: toSave, disconnected: false },
        { headers: { Authorization: `Bearer ${firebaseToken}` } }
      );

      const emailToCheck = toSave || currentUserEmail;
      const statusResponse = await apiClient.get(API_ENDPOINTS.lonewolfUserStatus(emailToCheck), {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const statusData = statusResponse?.data || statusResponse || {};

      if (statusData.active === true) {
        showToast('Lone Wolf account connected.');
        setIsEditingLoneWolfEmail(false);
        await loadLoneWolfStatus();
        return;
      }

      // Not Active — only an Active connection is allowed to persist. Roll back to whatever
      // was connected before this attempt (email AND disconnected flag) instead of leaving this
      // Invited/no-match email as "the current connection".
      await apiClient.patch(
        API_ENDPOINTS.updateLoneWolfEmail,
        { loneWolfEmail: previousSaved || '', disconnected: previousDisconnected },
        { headers: { Authorization: `Bearer ${firebaseToken}` } }
      );
      setLoneWolfEmailError(
        statusData.exists
          ? `${emailToCheck} has a Lone Wolf account, but it's still Invited, not Active — not saved. Try a different email, or wait until it's accepted.`
          : `No Lone Wolf account found for ${emailToCheck} — not saved.`
      );
      await loadLoneWolfStatus();
    } catch (err) {
      console.error('Failed to save Lone Wolf email:', err);
      if (err?.response?.status === 409) {
        setLoneWolfEmailError('This Lone Wolf account is already connected to another Rialty user.');
      } else {
        showToast('Failed to save. Please try again.', 'error');
      }
    } finally {
      setSavingLoneWolfEmail(false);
    }
  };

  const handleChangeLoneWolfAccount = () => {
    setLoneWolfEmailInput(loneWolfSavedEmail || currentUserEmail || '');
    setLoneWolfEmailError('');
    setIsEditingLoneWolfEmail(true);
  };

  const handleCancelLoneWolfEdit = () => {
    setLoneWolfEmailInput(loneWolfSavedEmail || currentUserEmail || '');
    setLoneWolfEmailError('');
    setIsEditingLoneWolfEmail(false);
  };

  // Lone Wolf's real web portal (its "Foundation" back-office product) confirmed live at
  // foundation.pre.lwolf.com — mirrors DocuSign's handleAddFormProvider (opens a real setup
  // URL in a new tab) now that we have one, instead of the earlier toast-only fallback used
  // when no portal URL was known. Form libraries still aren't self-service-enable-able from
  // within Rialty itself — the agent has to do it in Foundation (typically via their Broker
  // Admin) — but at least this takes them straight there instead of just naming who to ask.
  const handleLoneWolfAddFormProvider = () => {
    window.open('https://transact-workflow.lwolf.com/', '_blank', 'noopener,noreferrer');
  };

  // "Disconnect" here means clearing the saved override — there's no real session/token to
  // revoke (see canSeeLoneWolf comment above). If the login email itself happens to also be
  // Active on Lone Wolf, status will reflect that after clearing rather than going fully dark;
  // that's an inherent limit of an email-based override, not a bug.
  const handleDisconnectLoneWolf = () => {
    setConfirmModal({
      title: 'Disconnect Lone Wolf',
      message: 'This clears your saved Lone Wolf email. You can reconnect at any time by entering it again.',
      confirmLabel: 'Disconnect',
      onConfirm: doDisconnectLoneWolf,
    });
  };

  const doDisconnectLoneWolf = async () => {
    setSavingLoneWolfEmail(true);
    try {
      const firebaseToken = await getFirebaseToken();
      // disconnected:true — the explicit "I want this off" signal. loadLoneWolfStatus reads
      // this on future visits to skip the default login-email auto-check entirely, rather than
      // silently "reconnecting" just because the login email happens to be Active on Lone Wolf.
      await apiClient.patch(
        API_ENDPOINTS.updateLoneWolfEmail,
        { loneWolfEmail: '', disconnected: true },
        { headers: { Authorization: `Bearer ${firebaseToken}` } }
      );
      showToast('Lone Wolf account disconnected.');
      await loadLoneWolfStatus();
    } catch (err) {
      console.error('Failed to disconnect Lone Wolf:', err);
      showToast('Failed to disconnect. Please try again.', 'error');
    } finally {
      setSavingLoneWolfEmail(false);
    }
  };

  useEffect(() => {
    // CA is excluded from DocuSign account-status checks (same as Dashboard / Transactions /
    // create pages). The DocuSign card is never rendered for CA (canSeeDocuSign), so nothing
    // on the page reads the connection state for these users.
    // DocuSign is COLORADO-ONLY — only load DocuSign connection status for CO agents. Was
    // `!== 'CA'`, which needlessly loaded it for TX (and every non-CO, non-CA state).
    if (licenseState === 'CO' || licenseState === '') {
      loadConnectionStatus();
    }
    loadLoneWolfStatus();
  }, [loadConnectionStatus, loadLoneWolfStatus, licenseState]);

  // Re-check form-provider status when the user comes back to this tab after opening Lone
  // Wolf Foundation (handleLoneWolfAddFormProvider, a new tab, not a redirect-away-and-back
  // like DocuSign's OAuth flow) — otherwise they'd see the stale "not configured" warning
  // until a manual page refresh, even after actually enabling the library over there.
  // Only relevant once Active (form-provider status isn't loaded/shown otherwise).
  useEffect(() => {
    if (!loneWolfStatus.active) return;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      getFirebaseToken().then(loadLoneWolfFormProviderStatus).catch(() => {});
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loneWolfStatus.active, getFirebaseToken, loadLoneWolfFormProviderStatus]);

  const handleConnect = () => {
    if (connectionStatus.authUrl) {
      window.location.href = connectionStatus.authUrl;
    } else {
      showToast('Connect URL not available. Please try again.', 'error');
    }
  };

  const handleDisconnect = () => {
    setConfirmModal({
      title: 'Disconnect DocuSign',
      message: 'Are you sure you want to disconnect your DocuSign account? You will need to reconnect to use e-signatures.',
      confirmLabel: 'Disconnect',
      onConfirm: doDisconnect,
    });
  };

  const doDisconnect = async () => {
    setDisconnecting(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(API_ENDPOINTS.docusignDisconnect, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      showToast('DocuSign account disconnected.');
      setInviteInfo(null);
      setFormLibraries(null);
      await loadConnectionStatus();
    } catch (err) {
      console.error('Failed to disconnect DocuSign:', err);
      showToast('Failed to disconnect. Please try again.', 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleChangeAccount = () => {
    setConfirmModal({
      title: 'Change DocuSign Account',
      message: 'This will disconnect your current DocuSign account and redirect you to connect a new one. Continue?',
      confirmLabel: 'Continue',
      onConfirm: doChangeAccount,
    });
  };

  const doChangeAccount = async () => {
    setDisconnecting(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(API_ENDPOINTS.docusignDisconnect, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const response = await apiClient.get(API_ENDPOINTS.docusignConnect, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const data = response?.data || response || {};
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        showToast('Could not get connect URL. Please try again.', 'error');
        await loadConnectionStatus();
      }
    } catch (err) {
      console.error('Failed to change account:', err);
      showToast('Failed to change account. Please try again.', 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleResendInvite = async () => {
    if (!inviteInfo?.userId) return;
    setResendingInvite(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.docusignRoomsReinvite(inviteInfo.userId),
        {},
        { headers: { Authorization: `Bearer ${firebaseToken}` } }
      );
      showToast('Invitation resent successfully.');
    } catch (err) {
      console.error('Failed to resend invite:', err);
      showToast('Failed to resend invitation. Please try again.', 'error');
    } finally {
      setResendingInvite(false);
    }
  };

  // Send Invite — re-triggers the DocuSign Rooms company invite when connected
  // but no invite exists yet (auto-invite on OIDC callback silently failed).
  // Mirrors mobile's handleSendInvite, including the rate-limit cooldown that
  // relabels the button "Resend Invite" until it expires.
  const handleSendInvite = async () => {
    if (inviteBlockedUntil && Date.now() < inviteBlockedUntil) return;
    setSendingInvite(true);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(API_ENDPOINTS.docusignSendInvite, {}, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      showToast('Invitation sent. Check your email to accept the DocuSign invitation.');
      setInviteBlockedUntil(null);
      await loadConnectionStatus();
    } catch (err) {
      console.error('Failed to send invite:', err);
      const msg = err?.response?.data?.message || err.message || 'Failed to send invitation.';
      const rateLimited = /TooManyRequests|Limit resets at/i.test(msg);
      const cooldownMs = rateLimited ? 60 * 60 * 1000 : 60 * 1000;
      setInviteBlockedUntil(Date.now() + cooldownMs);
      showToast(rateLimited ? 'DocuSign has rate-limited this account. Please try again later.' : msg, 'error');
    } finally {
      setSendingInvite(false);
    }
  };

  // Add Form Provider — opens DocuSign setup URL (mirrors mobile handleAddFormProvider)
  const handleAddFormProvider = async () => {
    setSetupUrlLoading(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.docusignFormProviderSetupUrl, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const url = response?.url || response?.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        showToast('Setup URL not available. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Failed to get form provider setup URL:', err);
      showToast('Failed to open form provider setup. Please try again.', 'error');
    } finally {
      setSetupUrlLoading(false);
    }
  };

  const inviteStatusIsActive = inviteInfo?.status?.toLowerCase() === 'active';
  const inviteStatusIsPending = inviteInfo?.status?.toLowerCase() === 'pending';

  return (
    <div className="cs-page">

      {/* Confirm Modal */}
      {confirmModal && (
        <div className="cs-modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()}>
            <div className={`cs-modal-icon ${confirmModal.confirmLabel === 'Continue' ? 'cs-modal-icon-warning' : ''}`}>
              {confirmModal.confirmLabel === 'Continue' ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                  <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                </svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  <line x1="4" y1="4" x2="20" y2="20"/>
                </svg>
              )}
            </div>
            <h3 className="cs-modal-title">{confirmModal.title}</h3>
            <p className="cs-modal-message">{confirmModal.message}</p>
            <div className="cs-modal-actions">
              <button className="cs-modal-btn cs-modal-btn-cancel" onClick={() => setConfirmModal(null)}>
                Cancel
              </button>
              <button
                className={`cs-modal-btn cs-modal-btn-confirm ${confirmModal.confirmLabel === 'Continue' ? 'cs-modal-btn-confirm-primary' : ''}`}
                onClick={() => { setConfirmModal(null); confirmModal.onConfirm(); }}
              >
                {confirmModal.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`cs-toast cs-toast-${toast.type}`}>
          {toast.type === 'success' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          )}
          {toast.message}
        </div>
      )}

      <div className="cs-main-content">
        <div className="cs-content-box">
          <div className="cs-content">

            {/* Header */}
            <div className="cs-page-header">
              <div className="cs-page-header-left">
                <button className="cs-back-btn" onClick={() => navigate(-1)}>
                  <span>←</span>
                  <span>Back</span>
                </button>
                <h1 className="cs-page-title">Connected Services</h1>
                <p className="cs-page-subtitle">Manage your third-party integrations</p>
              </div>
            </div>

            {/* Lone Wolf Card — read-only status, no self-service connect/disconnect
                (accounts are provisioned manually by Rialty Support once notified) */}
            {canSeeLoneWolf && <div className="cs-service-card">
              {/* Card Header */}
              <div className="cs-service-card-header">
                <div className="cs-service-icon cs-service-icon-docusign">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div className="cs-service-card-info">
                  <h2 className="cs-service-name">Lone Wolf</h2>
                  <p className="cs-service-desc">E-signatures &amp; document management</p>
                </div>
                {!loneWolfLoading && !isEditingLoneWolfEmail && (
                  <div className={`cs-status-badge ${loneWolfStatus.active ? 'cs-status-connected' : 'cs-status-disconnected'}`}>
                    <span className="cs-status-dot"></span>
                    {loneWolfStatus.active ? 'Active' : (loneWolfStatus.exists ? 'Invited' : 'Not Invited')}
                  </div>
                )}
                {loneWolfLoading && <div className="cs-spinner cs-spinner-sm"></div>}
              </div>

              {/* Card Body */}
              {loneWolfLoading ? (
                <div className="cs-card-loading">
                  <div className="cs-spinner"></div>
                  <span>Loading status...</span>
                </div>
              ) : (isEditingLoneWolfEmail || !loneWolfStatus.active) ? (
                <div className="cs-card-body">
                  <p className="cs-not-connected-text">
                    {isEditingLoneWolfEmail
                      ? "Enter the email address your Lone Wolf account uses — this doesn't have to match your Rialty login email."
                      : loneWolfStatus.exists
                      ? `${loneWolfSavedEmail || currentUserEmail} has a Lone Wolf account, but it hasn't been activated yet. We've pre-filled your Rialty login email below — keep it as-is to check again, or enter a different one.`
                      : "Connect your LoneWolf account to enable e-signatures and document management within Rialty. We've pre-filled your Rialty login email below — change it if your Lone Wolf account uses a different address."}
                  </p>
                  <div className="invite-form-group">
                    <label className="invite-form-label">Lone Wolf Email Address</label>
                    <input
                      type="email"
                      className={`invite-form-input ${loneWolfEmailError ? 'email-error' : ''}`}
                      placeholder="you@example.com"
                      value={loneWolfEmailInput}
                      onChange={(e) => { setLoneWolfEmailInput(e.target.value); setLoneWolfEmailError(''); }}
                      disabled={savingLoneWolfEmail}
                    />
                    {loneWolfEmailError && (
                      <div className="email-check-message error">{loneWolfEmailError}</div>
                    )}
                  </div>
                  <div className="cs-card-actions">
                    <button className="cs-btn cs-btn-primary" onClick={handleSaveLoneWolfEmail} disabled={savingLoneWolfEmail}>
                      {savingLoneWolfEmail ? (
                        <><div className="cs-spinner cs-spinner-xs cs-spinner-white"></div>Connecting...</>
                      ) : (isEditingLoneWolfEmail || loneWolfStatus.exists ? 'Save & Check Status' : 'Connect Lone Wolf')}
                    </button>
                    {isEditingLoneWolfEmail && (
                      <button className="cs-btn cs-btn-secondary" onClick={handleCancelLoneWolfEdit} disabled={savingLoneWolfEmail}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ) : loneWolfStatus.active ? (
                <div className="cs-card-body">
                  <div className="cs-detail-fields">
                    <div className="cs-detail-field">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <span>{loneWolfSavedEmail || currentUserEmail}</span>
                    </div>
                    <div className="cs-invite-status cs-invite-active">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <span>Account Active</span>
                    </div>
                    {/* Form provider status — per licensed state, checked against the caller's
                        own accessible Lone Wolf libraries (mirrors DocuSign's form-libraries check) */}
                    {loneWolfFormProviderLoading && (
                      <div className="cs-detail-field cs-invite-loading">
                        <div className="cs-spinner cs-spinner-xs"></div>
                        <span>Checking form provider...</span>
                      </div>
                    )}
                    {!loneWolfFormProviderLoading && loneWolfFormProviderStatus && (
                      loneWolfFormProviderStatus.configured ? (
                        <div className="cs-detail-field">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                          </svg>
                          <span className="cs-lib-name">{loneWolfFormProviderStatus.libraryName} ({loneWolfFormProviderStatus.state})</span>
                        </div>
                      ) : (
                        <div className="cs-invite-status cs-invite-warning">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                          <span>{loneWolfFormProviderStatus.reason || 'No form provider configured'}</span>
                        </div>
                      )
                    )}
                  </div>
                  <div className="cs-card-actions">
                    {!loneWolfFormProviderLoading && loneWolfFormProviderStatus && !loneWolfFormProviderStatus.configured
                      && loneWolfFormProviderStatus.libraryAcronym && (
                      <button className="cs-btn cs-btn-primary" onClick={handleLoneWolfAddFormProvider}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                        </svg>
                        Add Form Provider
                      </button>
                    )}
                    <button className="cs-btn cs-btn-secondary" onClick={handleChangeLoneWolfAccount} disabled={savingLoneWolfEmail}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                        <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                      </svg>
                      Change Account
                    </button>
                    <button className="cs-btn cs-btn-disconnect" onClick={handleDisconnectLoneWolf} disabled={savingLoneWolfEmail}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        <line x1="4" y1="4" x2="20" y2="20"/>
                      </svg>
                      Disconnect
                    </button>
                  </div>
                </div>
              ) : null}
            </div>}

            {/* DocuSign Card — only shown to Agent and Broker roles */}
            {canSeeDocuSign && <div className="cs-service-card">
              {/* Card Header */}
              <div className="cs-service-card-header">
                <div className="cs-service-icon cs-service-icon-docusign">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <div className="cs-service-card-info">
                  <h2 className="cs-service-name">DocuSign</h2>
                  <p className="cs-service-desc">E-signatures &amp; document management</p>
                </div>
                {!connectionLoading && (
                  <div className={`cs-status-badge ${connectionStatus.connected ? 'cs-status-connected' : 'cs-status-disconnected'}`}>
                    <span className="cs-status-dot"></span>
                    {connectionStatus.connected ? 'Connected' : 'Not Connected'}
                  </div>
                )}
                {connectionLoading && <div className="cs-spinner cs-spinner-sm"></div>}
              </div>

              {/* Card Body */}
              {connectionLoading ? (
                <div className="cs-card-loading">
                  <div className="cs-spinner"></div>
                  <span>Loading status...</span>
                </div>
              ) : connectionStatus.connected ? (
                <div className="cs-card-body">
                  {/* Account Details + Invite Status + Form Libraries — all inline (mirrors mobile) */}
                  <div className="cs-detail-fields">

                    {/* Email */}
                    {connectionStatus.email && (
                      <div className="cs-detail-field">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        <span>{connectionStatus.email}</span>
                      </div>
                    )}

                    {/* Account Name */}
                    {connectionStatus.accountName && (
                      <div className="cs-detail-field">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 21h18M9 8h1m-1 4h1m-1 4h1m4-8h1m-1 4h1m-1 4h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>
                        </svg>
                        <span>{connectionStatus.accountName}</span>
                      </div>
                    )}

                    {/* Step 2: Invite Status — inline spinner while loading */}
                    {inviteLoading && (
                      <div className="cs-detail-field cs-invite-loading">
                        <div className="cs-spinner cs-spinner-xs"></div>
                        <span>Checking invitation status...</span>
                      </div>
                    )}
                    {!inviteLoading && inviteInfo && inviteStatusIsActive && (
                      <div className="cs-invite-status cs-invite-active">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        <span>Account Active</span>
                      </div>
                    )}
                    {/* Step 3: Form Libraries — inline spinner while loading (mirrors mobile) */}
                    {formLibrariesLoading && (
                      <div className="cs-detail-field cs-invite-loading">
                        <div className="cs-spinner cs-spinner-xs"></div>
                        <span>Checking form libraries...</span>
                      </div>
                    )}
                    {!formLibrariesLoading && inviteStatusIsActive && formLibraries !== null && (
                      formLibraries.length > 0 ? (
                        formLibraries.map((lib, idx) => (
                          <div key={lib.formsLibraryId || idx} className="cs-detail-field">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                              <line x1="16" y1="13" x2="8" y2="13"/>
                              <line x1="16" y1="17" x2="8" y2="17"/>
                            </svg>
                            <span className="cs-lib-name">{lib.name}</span>
                          </div>
                        ))
                      ) : (
                        <div className="cs-invite-status cs-invite-warning">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                          <span>No form libraries — add a form provider</span>
                        </div>
                      )
                    )}
                  </div>

                  {/* Actions */}
                  <div className="cs-card-actions">
                    {/* Send/Resend Invite — shown when connected but the invite hasn't been
                        accepted yet: either no invite record exists at all (auto-invite on
                        OIDC callback silently failed) or one is still pending. "Invite Pending"
                        stays visible above the button for as long as the button itself is
                        shown; once accepted (inviteStatusIsActive) both disappear together. */}
                    {!inviteLoading && (!inviteInfo || inviteStatusIsPending) && (() => {
                      const cooling = !!inviteBlockedUntil && Date.now() < inviteBlockedUntil;
                      const busy = inviteStatusIsPending ? resendingInvite : sendingInvite;
                      const buttonDisabled = busy || (!inviteStatusIsPending && cooling) || disconnecting;
                      const label = inviteStatusIsPending ? 'Resend Invite' : (cooling ? 'Resend Invite' : 'Resend Invite');
                      const handleClick = inviteStatusIsPending ? handleResendInvite : handleSendInvite;
                      return (
                        <div className="cs-invite-action-group">
                          <div className="cs-invite-status cs-invite-pending-status">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                            <span>Invite Pending</span>
                          </div>
                          <button
                            className="cs-btn cs-btn-primary"
                            onClick={handleClick}
                            disabled={buttonDisabled}
                          >
                            {busy ? (
                              <>
                                <div className="cs-spinner cs-spinner-xs cs-spinner-white"></div>
                                Sending...
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                  <polyline points="22,6 12,13 2,6"/>
                                </svg>
                                {label}
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })()}
                    {!inviteStatusIsPending && !inviteLoading && inviteInfo && (
                      <button
                        className="cs-btn cs-btn-primary"
                        onClick={handleAddFormProvider}
                        disabled={setupUrlLoading || (formLibraries !== null && formLibraries.length > 0)}
                      >
                        {setupUrlLoading ? (
                          <>
                            <div className="cs-spinner cs-spinner-xs cs-spinner-white"></div>
                            Loading...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                              <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                            </svg>
                            Add Form Provider
                          </>
                        )}
                      </button>
                    )}
                    <button className="cs-btn cs-btn-secondary" onClick={handleChangeAccount} disabled={disconnecting}>
                      {disconnecting ? (
                        <><div className="cs-spinner cs-spinner-xs"></div>Processing...</>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                            <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                          </svg>
                          Change Account
                        </>
                      )}
                    </button>
                    <button className="cs-btn cs-btn-disconnect" onClick={handleDisconnect} disabled={disconnecting}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        <line x1="4" y1="4" x2="20" y2="20"/>
                      </svg>
                      Disconnect
                    </button>
                  </div>
                </div>
              ) : (
                <div className="cs-card-body">
                  <p className="cs-not-connected-text">
                    Connect your DocuSign account to enable e-signatures and document management within Rialty.
                  </p>
                  <button className="cs-btn cs-btn-primary cs-btn-connect" onClick={handleConnect}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                    Connect DocuSign
                  </button>
                </div>
              )}
            </div>}

            {/* Google Drive Card */}
            <div className="cs-service-card cs-service-card-disabled">
              <div className="cs-service-card-header">
                <div className="cs-service-icon cs-service-icon-drive">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 19h20L12 2z" stroke="#4285F4" fill="none"/>
                    <path d="M2 19l6-10 4 7-10 3z" stroke="#0F9D58" fill="none"/>
                    <path d="M22 19l-6-10-4 7 10 3z" stroke="#FBBC04" fill="none"/>
                  </svg>
                </div>
                <div className="cs-service-card-info">
                  <h2 className="cs-service-name cs-service-name-muted">Google Drive</h2>
                  <p className="cs-service-desc">Cloud storage &amp; file sync</p>
                </div>
                <div className="cs-status-badge cs-status-coming-soon">Coming Soon</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectedServicesPage;
